<script setup lang="ts">
import FormColorPicker from '@/components/ui/ColorPicker/ColorPicker.vue'
import ImageUploaderNew from '@/components/ui/ImageUploader/ImageUploaderNew.vue'
import CopyLink from '@/components/CopyLink.vue'
import PreviewZone from './PreviewZone.vue'
import QrcodeVue from 'qrcode.vue'

import useWebappTabs from '../composables/webappPageTabs'

import type { ImageFile } from '@/types/types'

withDefaults(
  defineProps<{
    color: string
    deletedImages: number[]
    images: ImageFile[]
    initialImages: ImageFile[]
    shareLink: string
  }>(),
  {
    color: '#000000',
    deletedImages: () => [],
    images: () => [],
    initialImages: () => [],
    shareLink: ''
  }
)

const { shareTabs, currentShareTab } = useWebappTabs()
const imageUploader = ref<InstanceType<typeof ImageUploaderNew> | null>(null)
const emit = defineEmits<{
  (e: 'update:color', value: string): void
  (e: 'update:images', value: string[]): void
  (e: 'update:deleted-images', value: number[]): void
}>()

const { t } = useI18n()

const updateImages = (images: string[]) => {
  emit('update:images', images)
}

const updateColor = (value: string) => {
  emit('update:color', value)
}

const deleteImages = (images: number[]) => {
  emit('update:deleted-images', images)
}

const downloadQrCode = () => {
  try {
    const canvas = document.getElementById('qrCode') as HTMLCanvasElement

    if (canvas) {
      const link = document.createElement('a')
      link.download = 'qrcode.png'
      link.href = canvas.toDataURL('image/png')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  } catch (error) {
    console.error('Erreur lors du téléchargement du QR code:', error)
  }
}

const generateFlyer = () => {
  const token = localStorage.getItem('jwt')

  window.open(`https://visuals.heypongo.com/?source=addons&type=plv&token=${token}`, '_blank')
}

defineExpose({
  resetImages: () => {
    imageUploader.value?.resetImages()
  }
})
</script>

<template>
  <div class="flex flex-col gap-5">
    <PreviewZone zone="loyalty" class="flex flex-col gap-5">
      <h3 class="text-base font-sans font-semibold">{{ t('pages.loyaltySupports.webapp.appearance.colors') }}</h3>
      <div class="flex flex-col gap-2">
        <p class="text-sm">{{ t('pages.loyaltySupports.webapp.appearance.primaryColor') }}</p>
        <FormColorPicker id="primary.color" :model-value="color" @update:model-value="updateColor" />
      </div>
    </PreviewZone>

    <PreviewZone zone="home" class="flex flex-col gap-5">
      <h3 class="text-base font-sans font-semibold">{{ t('pages.loyaltySupports.webapp.appearance.headerImage') }}</h3>
      <div class="flex flex-col gap-2">
        <ImageUploaderNew
          ref="imageUploader"
          :images="images"
          :initial-images="initialImages"
          :deleted-images="deletedImages"
          :aspect-ratio="[200, 385]"
          @update:images="updateImages"
          @update:deleted-images="deleteImages"
        />
      </div>
    </PreviewZone>

    <div class="flex flex-col gap-5 bg-white rounded-xl p-5 border border-border">
      <h3 class="text-base font-sans font-semibold">{{ t('pages.loyaltySupports.webapp.appearance.share') }}</h3>
      <BaseTab v-model="currentShareTab" :tabs="shareTabs" disable-router />
      <div class="flex flex-col gap-2">
        <template v-if="currentShareTab === 'link'">
          <p class="text-sm">{{ t('pages.loyaltySupports.webapp.appearance.shareLink') }}</p>
          <CopyLink id="shareLink" :link="shareLink" />
        </template>
        <template v-if="currentShareTab === 'qrCode'">
          <p class="text-sm">{{ t('pages.loyaltySupports.webapp.appearance.qrCodeTab') }}</p>
          <qrcode-vue id="qrCode" :value="shareLink" :size="96" level="H" render-as="canvas" />
          <Button variant="outline" class="max-w-max" @click="downloadQrCode">
            {{ t('pages.loyaltySupports.webapp.appearance.downloadQrCode') }}
          </Button>
        </template>
        <template v-if="currentShareTab === 'flyer'">
          <Button variant="outline" class="max-w-max" @click="generateFlyer">
            {{ t('pages.loyaltySupports.webapp.appearance.downloadFlyer') }}
          </Button>
        </template>
      </div>
    </div>
  </div>
</template>
